import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", vehicle: "", service: "", message: "" };

export const QuoteForm = () => {
  const { t } = useI18n();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const options = t("quote.serviceOptions");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.vehicle || !form.service) {
      toast.error(t("quote.errRequired"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success(t("quote.okToast"));
      setForm(empty);
    } catch (err) {
      toast.error(t("quote.errGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0F1115] border-white/10 text-white placeholder:text-neutral-600 h-12 rounded-xl focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400";

  return (
    <section id="devis" data-testid="quote-section" className="relative py-28 md:py-36 px-6 md:px-10 bg-[#070809] border-t border-cyan-500/10 overflow-hidden">
      <div className="absolute inset-0 radial-cyan pointer-events-none" />
      <div className="relative max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan">{t("quote.tag")}</span>
          <h2 className="font-display font-700 uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-none">
            {t("quote.title1")} <br />
            <span className="text-cyan neon-text-glow">{t("quote.title2")}</span>
          </h2>
          <p className="text-neutral-400 font-light mt-6 max-w-md text-lg">{t("quote.subtitle")}</p>
          <p
            data-testid="prelaunch-quote-hint"
            className="mt-5 max-w-md text-sm text-neutral-400 border-l-2 border-cyan-400/50 pl-4 font-light"
          >
            {t("preLaunch.quoteHint")}
          </p>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="glass rounded-3xl border border-cyan-500/15 p-7 md:p-9 space-y-5"
        >
          {done ? (
            <div data-testid="quote-success" className="flex flex-col items-center text-center py-10 gap-4">
              <CheckCircle2 size={54} className="text-cyan" />
              <h3 className="font-display font-600 uppercase text-2xl">{t("quote.successTitle")}</h3>
              <p className="text-neutral-400 font-light">{t("quote.successMsg")}</p>
              <button type="button" data-testid="quote-reset-btn" onClick={() => setDone(false)} className="mt-2 font-mono-accent text-xs uppercase tracking-[0.2em] text-cyan">
                {t("quote.newRequest")}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-neutral-300 text-xs uppercase tracking-wider">{t("quote.name")}</Label>
                  <Input data-testid="quote-name" value={form.name} onChange={set("name")} placeholder={t("quote.namePh")} className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300 text-xs uppercase tracking-wider">{t("quote.phone")}</Label>
                  <Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} placeholder={t("quote.phonePh")} className={inputCls} />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 text-xs uppercase tracking-wider">{t("quote.email")}</Label>
                <Input data-testid="quote-email" type="email" value={form.email} onChange={set("email")} placeholder={t("quote.emailPh")} className={inputCls} />
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 text-xs uppercase tracking-wider">{t("quote.vehicle")}</Label>
                <Input data-testid="quote-vehicle" value={form.vehicle} onChange={set("vehicle")} placeholder={t("quote.vehiclePh")} className={inputCls} />
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 text-xs uppercase tracking-wider">{t("quote.service")}</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="quote-service" className={inputCls}>
                    <SelectValue placeholder={t("quote.servicePh")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1115] border-white/10 text-white">
                    {options.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`service-opt-${o}`} className="focus:bg-cyan-400/20 focus:text-cyan">
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 text-xs uppercase tracking-wider">{t("quote.message")}</Label>
                <Textarea data-testid="quote-message" value={form.message} onChange={set("message")} placeholder={t("quote.messagePh")} className={`${inputCls} h-24 py-3`} />
              </div>

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 text-black font-medium text-base py-4 rounded-xl neon-glow hover:brightness-110 transition-all duration-300 disabled:opacity-60"
                style={{ backgroundColor: "#C9A96A" }}
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> {t("quote.sending")}
                  </>
                ) : (
                  t("quote.submit")
                )}
              </button>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
};
